import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';

const StagingTrayContext = createContext();

const STORAGE_KEY = 'stagingTrayItems';

const trayReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_ITEM':
      if (state.items.find(item => item.id === action.payload.id)) {
        return state; // Don't add duplicates
      }
      return {
        ...state,
        items: [...state.items, action.payload]
      };
    
    case 'REMOVE_ITEM':
      const filteredItems = state.items.filter(item => item.id !== action.payload.id);
      return {
        ...state,
        items: filteredItems,
        isOpen: filteredItems.length > 0 ? state.isOpen : false
      };
    
    case 'CLEAR_TRAY':
      return {
        ...state,
        items: [],
        isOpen: false
      };
    
    case 'TOGGLE_TRAY':
      return {
        ...state,
        isOpen: !state.isOpen
      };
    
    case 'SET_TRAY_OPEN':
      return {
        ...state,
        isOpen: action.payload
      };
    
    default:
      return state;
  }
};

const getInitialState = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return { items: JSON.parse(saved), isOpen: false };
  } catch {}
  return { items: [], isOpen: false };
};

export const StagingTrayProvider = ({ children }) => {
  const [state, dispatch] = useReducer(trayReducer, undefined, getInitialState);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state.items));
    } catch {}
  }, [state.items]);

  const addItem = useCallback((item) => {
    dispatch({ type: 'ADD_ITEM', payload: item });
  }, []);

  const removeItem = useCallback((item) => {
    dispatch({ type: 'REMOVE_ITEM', payload: item });
  }, []);

  const clearTray = useCallback(() => {
    dispatch({ type: 'CLEAR_TRAY' });
  }, []);

  const toggleTray = useCallback(() => {
    dispatch({ type: 'TOGGLE_TRAY' });
  }, []);

  const setTrayOpen = useCallback((isOpen) => {
    dispatch({ type: 'SET_TRAY_OPEN', payload: isOpen });
  }, []);

  const isInTray = useCallback((item) => {
    return state.items.some(trayItem => trayItem.id === item.id);
  }, [state.items]);

  const value = {
    items: state.items,
    isOpen: state.isOpen,
    itemCount: state.items.length,
    addItem,
    removeItem,
    clearTray,
    toggleTray,
    setTrayOpen,
    isInTray
  };

  return (
    <StagingTrayContext.Provider value={value}>
      {children}
    </StagingTrayContext.Provider>
  );
};

export const useStagingTray = () => {
  const context = useContext(StagingTrayContext);
  if (!context) {
    throw new Error('useStagingTray must be used within a StagingTrayProvider');
  }
  return context;
};